// ===========================================
// Admin React Query Hooks
// ===========================================
// Custom hooks for admin API operations with React Query

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { lessonKeys } from './useLessons';
import { hybridBookingKeys } from './useHybridBooking';
import { pricingKeys } from './usePricing';
import {
  termsApi,
  locationsApi,
  roomsApi,
  instrumentsApi,
  lessonTypesApi,
  lessonDurationsApi,
  ageGroupsApi,
  programLevelsApi,
  streamsApi,
  eventPricingStructuresApi,
  schoolApi,
  Term,
  Location,
  Room,
  Instrument,
  LessonType,
  LessonDuration,
  AgeGroup,
  ProgramLevel,
  Stream,
  EventPricingStructureInput,
  SchoolSettings,
} from '../api/admin.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const adminKeys = {
  all: ['admin'] as const,
  school: () => [...adminKeys.all, 'school'] as const,
  terms: () => [...adminKeys.all, 'terms'] as const,
  term: (id: string) => [...adminKeys.terms(), id] as const,
  locations: () => [...adminKeys.all, 'locations'] as const,
  location: (id: string) => [...adminKeys.locations(), id] as const,
  rooms: (locationId?: string) => [...adminKeys.all, 'rooms', locationId] as const,
  room: (id: string) => [...adminKeys.all, 'rooms', 'detail', id] as const,
  instruments: () => [...adminKeys.all, 'instruments'] as const,
  lessonTypes: () => [...adminKeys.all, 'lessonTypes'] as const,
  lessonDurations: () => [...adminKeys.all, 'lessonDurations'] as const,
  ageGroups: () => [...adminKeys.all, 'ageGroups'] as const,
  programLevels: () => [...adminKeys.all, 'programLevels'] as const,
  streams: () => [...adminKeys.all, 'streams'] as const,
  eventPricingStructures: () => [...adminKeys.all, 'eventPricingStructures'] as const,
};

// ===========================================
// SCHOOL SETTINGS HOOKS
// ===========================================

// The admin list reads take `{ enabled }` so a page shown to callers without the route's
// permission can skip them (#272). For instruments and program levels, `hooks/useViewOptions.ts`
// reads the calendar picker list instead; the others have no picker list (plan D3/D4).
export function useSchoolSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.school(),
    queryFn: schoolApi.getSettings,
    enabled: options.enabled ?? true,
  });
}

export function useUpdateSchoolSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<SchoolSettings>) => schoolApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.school() });
    },
  });
}

// ===========================================
// TERMS HOOKS
// ===========================================

export function useTerms(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.terms(),
    queryFn: termsApi.getAll,
    enabled: options.enabled ?? true,
  });
}

export function useTerm(id: string) {
  return useQuery({
    queryKey: adminKeys.term(id),
    queryFn: () => termsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateTerm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; startDate: string; endDate: string }) =>
      termsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.terms() });
    },
  });
}

export function useUpdateTerm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Term> }) =>
      termsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.terms() });
    },
  });
}

export function useDeleteTerm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => termsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.terms() });
    },
  });
}

/** Reopening / relocking changes what every class surface may do, so lesson, booking and term queries refresh. */
function invalidateAfterLockChange(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: adminKeys.terms() });
  queryClient.invalidateQueries({ queryKey: lessonKeys.all });
  queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
}

export function useReopenTerm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason, days }: { id: string; reason: string; days: number }) =>
      termsApi.reopen(id, { reason, days }),
    onSuccess: () => invalidateAfterLockChange(queryClient),
  });
}

export function useRelockTerm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => termsApi.relock(id),
    onSuccess: () => invalidateAfterLockChange(queryClient),
  });
}

// ===========================================
// LOCATIONS HOOKS
// ===========================================

export function useLocations(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.locations(),
    queryFn: locationsApi.getAll,
    enabled: options.enabled ?? true,
  });
}

export function useLocation(id: string) {
  return useQuery({
    queryKey: adminKeys.location(id),
    queryFn: () => locationsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      streetAddress?: string;
      unit?: string;
      suburb?: string;
      state?: string;
      postcode?: string;
      country?: string;
      phone?: string;
    }) => locationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.locations() });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Location> & {
        latitude?: number | null;
        longitude?: number | null;
        regeocode?: boolean;
      };
    }) => locationsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.locations() });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => locationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.locations() });
      enqueueSnackbar('Location deleted successfully', { variant: 'success' });
    },
  });
}

// ===========================================
// ROOMS HOOKS
// ===========================================

export function useRooms(locationId?: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.rooms(locationId),
    queryFn: () => roomsApi.getAll(locationId),
    enabled: options.enabled ?? true,
  });
}

export function useRoom(id: string) {
  return useQuery({
    queryKey: adminKeys.room(id),
    queryFn: () => roomsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { locationId: string; name: string; capacity?: number }) =>
      roomsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.rooms() });
      queryClient.invalidateQueries({ queryKey: adminKeys.locations() });
    },
  });
}

export function useUpdateRoom() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Room> }) =>
      roomsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.rooms() });
      enqueueSnackbar('Room updated successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update room';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useDeleteRoom() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => roomsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.rooms() });
      queryClient.invalidateQueries({ queryKey: adminKeys.locations() });
      enqueueSnackbar('Room deleted successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete room';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// INSTRUMENTS HOOKS
// ===========================================

export function useInstruments(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.instruments(),
    queryFn: instrumentsApi.getAll,
    enabled: options.enabled ?? true,
  });
}

export function useCreateInstrument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; sortOrder?: number; color?: string }) =>
      instrumentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.instruments() });
    },
  });
}

export function useUpdateInstrument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Instrument> }) =>
      instrumentsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.instruments() });
    },
  });
}

export function useDeleteInstrument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => instrumentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.instruments() });
    },
  });
}

export function useReorderInstruments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (instrumentIds: string[]) => instrumentsApi.reorder(instrumentIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.instruments() });
    },
  });
}

// ===========================================
// LESSON TYPES HOOKS
// ===========================================

export function useLessonTypes(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.lessonTypes(),
    queryFn: lessonTypesApi.getAll,
    enabled: options.enabled ?? true,
  });
}

export function useCreateLessonType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      type: 'INDIVIDUAL' | 'GROUP' | 'BAND' | 'HYBRID';
      defaultDuration: number;
      description?: string;
      color?: string;
      allowPartnerOverlap?: boolean;
    }) => lessonTypesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.lessonTypes() });
    },
  });
}

export function useUpdateLessonType() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LessonType> }) =>
      lessonTypesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.lessonTypes() });
    },
    // The page swallows the rejection to keep the dialog open, so the reason must be shown
    // here — e.g. a type change refused because classes use it (#378).
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update class type';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useDeleteLessonType() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => lessonTypesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.lessonTypes() });
      enqueueSnackbar('Class type deleted successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete class type';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// LESSON DURATIONS HOOKS
// ===========================================

/**
 * The Class Pricing tables draw one column per duration and one row per program level, and
 * cache for the default 5 minutes — refresh them (and the "prices invoicing needs" check) when
 * either list changes, or a new length only appears after a manual reload.
 */
function invalidatePricingTables(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: pricingKeys.matrix() });
  queryClient.invalidateQueries({ queryKey: pricingKeys.flexibleMatrix() });
}

export function useLessonDurations(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.lessonDurations(),
    queryFn: lessonDurationsApi.getAll,
    enabled: options.enabled ?? true,
  });
}

export function useCreateLessonDuration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { minutes: number }) => lessonDurationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.lessonDurations() });
      invalidatePricingTables(queryClient);
    },
  });
}

export function useUpdateLessonDuration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LessonDuration> }) =>
      lessonDurationsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.lessonDurations() });
      invalidatePricingTables(queryClient);
    },
  });
}

export function useDeleteLessonDuration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => lessonDurationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.lessonDurations() });
      invalidatePricingTables(queryClient);
    },
  });
}

// ===========================================
// AGE GROUP HOOKS
// ===========================================

export function useAgeGroups() {
  return useQuery({
    queryKey: adminKeys.ageGroups(),
    queryFn: ageGroupsApi.getAll,
  });
}

export function useCreateAgeGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; minAge?: number; maxAge?: number; sortOrder?: number; color?: string }) =>
      ageGroupsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.ageGroups() });
    },
  });
}

export function useUpdateAgeGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AgeGroup> }) =>
      ageGroupsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.ageGroups() });
    },
  });
}

export function useDeleteAgeGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ageGroupsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.ageGroups() });
    },
  });
}

export function useReorderAgeGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ageGroupIds: string[]) => ageGroupsApi.reorder(ageGroupIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.ageGroups() });
    },
  });
}

// ===========================================
// PROGRAM LEVEL HOOKS
// ===========================================

export function useProgramLevels(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.programLevels(),
    queryFn: programLevelsApi.getAll,
    enabled: options.enabled ?? true,
  });
}

export function useCreateProgramLevel() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: { name: string; sortOrder?: number; color?: string }) =>
      programLevelsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.programLevels() });
      invalidatePricingTables(queryClient);
      enqueueSnackbar('Program level created successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create program level';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useUpdateProgramLevel() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ProgramLevel> }) =>
      programLevelsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.programLevels() });
      invalidatePricingTables(queryClient);
      enqueueSnackbar('Program level updated successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update program level';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useDeleteProgramLevel() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => programLevelsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.programLevels() });
      invalidatePricingTables(queryClient);
      enqueueSnackbar('Program level deleted successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete program level';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useReorderProgramLevels() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (programLevelIds: string[]) => programLevelsApi.reorder(programLevelIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.programLevels() });
      invalidatePricingTables(queryClient);
    },
  });
}

// ===========================================
// STREAM HOOKS
// ===========================================

export function useStreams(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.streams(),
    queryFn: streamsApi.getAll,
    enabled: options.enabled ?? true,
  });
}

export function useCreateStream() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: { name: string; sortOrder?: number; color?: string | null; individualSlotDuration?: number | null }) =>
      streamsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.streams() });
      enqueueSnackbar('Stream created successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create stream';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useUpdateStream() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Stream> }) =>
      streamsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.streams() });
      enqueueSnackbar('Stream updated successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update stream';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useDeleteStream() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => streamsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.streams() });
      enqueueSnackbar('Stream deleted successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete stream';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useReorderStreams() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (streamIds: string[]) => streamsApi.reorder(streamIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.streams() });
    },
  });
}

// ===========================================
// EVENT PRICING STRUCTURE HOOKS
// ===========================================
// Every mutation surfaces the SERVER's message verbatim on error — the backend
// returns actionable 400s the admin needs to read, e.g. "Cannot delete an active
// pricing structure. Set to inactive first." and "Cannot delete the default
// pricing structure. Make another structure the default first."

export function useEventPricingStructures() {
  return useQuery({
    queryKey: adminKeys.eventPricingStructures(),
    queryFn: eventPricingStructuresApi.getAll,
  });
}

export function useCreateEventPricingStructure() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: EventPricingStructureInput) => eventPricingStructuresApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.eventPricingStructures() });
      enqueueSnackbar('Pricing structure created', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create pricing structure';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useUpdateEventPricingStructure() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EventPricingStructureInput> }) =>
      eventPricingStructuresApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.eventPricingStructures() });
      enqueueSnackbar('Pricing structure updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update pricing structure';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useDeleteEventPricingStructure() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => eventPricingStructuresApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.eventPricingStructures() });
      enqueueSnackbar('Pricing structure deleted', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      // The inactive-first and default-first 400s land here, verbatim.
      const message = error.response?.data?.message || 'Failed to delete pricing structure';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useReorderEventPricingStructures() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (structureIds: string[]) => eventPricingStructuresApi.reorder(structureIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.eventPricingStructures() });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to reorder pricing structures';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useSetDefaultEventPricingStructure() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => eventPricingStructuresApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.eventPricingStructures() });
      enqueueSnackbar('Default pricing structure updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to set the default';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
